import { memo, useMemo } from 'react';

import { Portal } from '@air/primitive-portal';

import { PublicAssetMobileCommentsPanel } from '~/_app/(public-asset)/_components/PublicAssetMobileCommentsPanel';
import { PublicMobileAssetModalNavigation } from '~/_app/(public-asset)/_components/PublicAssetMobileNavigation';
import { PublicMobileHeader } from '~/components/AssetModal/components/PublicMobileHeader';
import { PublicMobileVersionsPanelWrapper } from '~/components/AssetModal/MobileTabNavigation/MobileVersionsPanelWrapper/PublicMobileVersionsPanelWrapper';
import { MobilePDFPageControls } from '~/components/AssetModalV2/Visualizer/PDFVisualizer/PDFControls/components/MobilePDFPageControls';
import { PublicPDFVisualizer } from '~/components/AssetModalV2/Visualizer/PDFVisualizer/PublicPDFVisualizer';
import { PublicAssetModalInfoPanel } from '~/components/PublicAssetModal/components/PublicAssetModalInfoPanel';
import { PublicVideoVisualizer } from '~/components/PublicAssetModal/components/PublicVideoVisualizer';
import { PublicAssetModalMobileBanner } from '~/components/PublicAssetModal/shared/components/PublicAssetModalMobileBanner/PublicAssetModalMobileBanner';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { AssetModalMobileLayout } from '~/components/v2/components/asset-modal/mobile/AssetModalMobileLayout';
import { useCanViewAllVersionsOnPublicShareLink } from '~/hooks/useCanViewAllVersionsOnPublicShareLink';
import { AssetModalPanel, useAssetModalPanelContext } from '~/providers/AssetModalPanelContextProvider';
import { MobileAssetModalContextProvider } from '~/providers/MobileAssetModalContextProvider';
import { canViewAssetDiscussions } from '~/utils/permissions/assetPermissions';

export const PublicClipAssetModalMobileLayout = memo(() => {
  const { panel, hidePanel } = useAssetModalPanelContext();

  const { permissions } = usePublicPermissions();

  const canViewDiscussions = canViewAssetDiscussions(permissions);
  const { canViewAllVersions } = useCanViewAllVersionsOnPublicShareLink();

  const content = useMemo(() => {
    let panelOverlay = null;

    if (panel === AssetModalPanel.COMMENTS && canViewDiscussions) {
      panelOverlay = (
        <Portal>
          <div className="fixed inset-0 flex flex-col items-start justify-start overflow-y-auto ads:bg-base bg-gray-1">
            <PublicAssetMobileCommentsPanel />
          </div>
        </Portal>
      );
    } else if (panel === AssetModalPanel.VERSIONS && canViewAllVersions) {
      panelOverlay = <PublicMobileVersionsPanelWrapper onClose={hidePanel} />;
    }

    return (
      <>
        {panelOverlay}
        <PublicAssetModalInfoPanel />
      </>
    );
  }, [canViewAllVersions, canViewDiscussions, hidePanel, panel]);

  const header = useMemo(() => <PublicMobileHeader />, []);

  return (
    <MobileAssetModalContextProvider stickOnTop={true}>
      <AssetModalMobileLayout
        Banner={PublicAssetModalMobileBanner}
        PDFVisualizer={PublicPDFVisualizer}
        VideoVisualizer={PublicVideoVisualizer}
        panel={content}
        header={header}
        navigation={<PublicMobileAssetModalNavigation />}
        visualizerOverlay={<MobilePDFPageControls />}
      />
    </MobileAssetModalContextProvider>
  );
});

PublicClipAssetModalMobileLayout.displayName = 'PublicClipAssetModalMobileLayout';
